import { H2 } from "@sellar-io/components";
import {
  BusinessGetResponseType,
  StorefrontLinkGetResponseType,
  UserCreateResponseType,
} from "@sellar-io/api-v2/types";
import { ReactElement, useEffect } from "react";
import { useMatch } from "react-router";
import { useSearchParams } from "react-router";
import { SpinnerLegacy } from "../../../../components";
import * as ROUTES from "../../../../constants/routes";
import { SIGNUP } from "../../../../constants/routes";
import { SignUpFlowStepType } from "../../../../contexts/SignupContext";
import { useEvents, useSellarUser } from "../../../../hooks";
import { useStorefrontLink } from "../../../../hooks/api/v2/storefrontLink/useStorefrontLink";
import { useSupplier } from "../../../../hooks/api/v2/supplier/useSupplier";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../hooks/useSignupContext.shared";

import { SignInForm } from "../../signIn/SignInForm";

const getNextSigninStep = ({
  showLoginTitle,
  signupDialogActive,
  endOnboarding,
  setStep,
  user,
  business,
  storefrontLink,
  basketTotal,
  setBuyerSelectBusinessId,
  changeBusiness,
}: {
  showLoginTitle: boolean;
  signupDialogActive: boolean;
  endOnboarding: () => void;
  setStep: (step: SignUpFlowStepType) => void;
  user: UserCreateResponseType | null;
  business: BusinessGetResponseType | null;
  storefrontLink?: StorefrontLinkGetResponseType;
  setBuyerSelectBusinessId: (buyerSelectBusinessId: number | undefined) => void;
  basketTotal?: number;
  changeBusiness: (businessId: number) => void;
}) => {
  if (user && !business) return setStep(SignupFlowStep.BuyerCreationStep);

  const buyerBusinessUsers = user?.BusinessUsers.filter(
    (businessUser) => businessUser.Business.type === "retailer",
  );
  const hasBasketTotal = basketTotal || basketTotal === 0;

  const storefrontLinkBusinessId =
    storefrontLink?.SupplierRetailerConnection.Retailer.Business.id;
  if (
    storefrontLinkBusinessId &&
    hasBasketTotal &&
    buyerBusinessUsers?.length
  ) {
    // If the storefront link contains a connection ID with an attached business,
    // and the user signing in has that business attached & are placing an order
    // Then sign them in as that business
    const storefrontLinkBuyerBusinessUser = buyerBusinessUsers.find(
      (businessUser) => businessUser.Business.id === storefrontLinkBusinessId,
    );
    const businessId = storefrontLinkBuyerBusinessUser?.Business.id;
    if (businessId) {
      setBuyerSelectBusinessId(businessId);
      changeBusiness(businessId);
      return setStep(SignupFlowStep.ConfirmationStep);
    }
  }
  const hasMultipleVenues = buyerBusinessUsers && buyerBusinessUsers.length > 1;
  const hasSupplierWithNoBuyerVenues = !buyerBusinessUsers?.length && business;
  if (
    user &&
    signupDialogActive &&
    hasBasketTotal &&
    (hasMultipleVenues || hasSupplierWithNoBuyerVenues)
  ) {
    return setStep(SignupFlowStep.BusinessSelectStep);
  }
  if (user && business) {
    if (signupDialogActive && !showLoginTitle) {
      return setStep(SignupFlowStep.ConfirmationStep);
    }
    return endOnboarding();
  }
};
export function UserSignInStep({ email }: { email?: string }): ReactElement {
  const { track } = useEvents();
  const { loading, user, changeBusiness } = useSellarUser();
  const {
    setStep,
    endOnboarding,
    showLoginTitle,
    signupDialogActive,
    basketTotal,
    setBuyerSelectBusinessId,
  } = useSignupContext();
  const { business, loading: sellarCurrentBusinessLoading } =
    useSellarCurrentBusiness();
  const [searchParams] = useSearchParams();

  const matchedSupplierParams = useMatch(ROUTES.ORDER_BUYER.pattern);
  const supplierQueryEnabled =
    !!matchedSupplierParams?.params.supplierIdOrUrlId;
  const supplierQuery = useSupplier(
    matchedSupplierParams?.params.supplierIdOrUrlId,
    {
      enabled: supplierQueryEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const storefrontLinkPassword = searchParams.get("storefrontLink");
  const storefrontLinkQueryEnabled =
    !!storefrontLinkPassword && !!supplierQuery.data;

  const storefrontLinkQuery = useStorefrontLink(
    storefrontLinkPassword,
    {
      SupplierId: supplierQuery.data?.id ?? 0,
    },
    {
      enabled: storefrontLinkQueryEnabled,
    },
  );

  useEffect(() => {
    track("Sign In Page Viewed");
  }, [track]);

  const isSignupRoute = !!useMatch(SIGNUP);
  const isStorefrontLinkLoading =
    storefrontLinkQuery.isPending && storefrontLinkQueryEnabled;
  const isSupplierLoading = supplierQuery.isPending && supplierQueryEnabled;
  if (
    loading ||
    sellarCurrentBusinessLoading ||
    isStorefrontLinkLoading ||
    isSupplierLoading
  )
    return <SpinnerLegacy />;

  getNextSigninStep({
    showLoginTitle,
    signupDialogActive,
    endOnboarding,
    setStep,
    user,
    business,
    basketTotal,
    storefrontLink: storefrontLinkQuery.data,
    setBuyerSelectBusinessId,
    changeBusiness,
  });
  return (
    <div className={"flex h-full flex-col justify-center gap-4"}>
      <div className={"flex flex-col gap-2"}>
        <H2 className={"text-center"}>
          {isSignupRoute
            ? "Looks like you already have a Sellar account. Log in."
            : "Enter your password to log in"}
        </H2>
      </div>
      <SignInForm email={email} />
    </div>
  );
}
